import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Sparkles, Copy, RefreshCw, AlertCircle, Hash, Check, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface CaptionVariant {
  style: string;
  caption: string;
}
interface HashtagTiers {
  broad: string[];
  mid: string[];
  niche: string[];
}
interface AIResult {
  captions: CaptionVariant[];
  hashtags: HashtagTiers;
}

const POST_TYPES = ['Feed Post', 'Reel', 'Carousel', 'Story'];
const styleAccent: Record<string, string> = {
  Cinematic: 'border-l-[#1A3B5C]',
  Conversational: 'border-l-[#C9A961]',
  Editorial: 'border-l-emerald-600',
};

const CaptionTool: React.FC = () => {
  const [theme, setTheme] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [postType, setPostType] = useState('Feed Post');
  const [existingCaption, setExistingCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AIResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [tiers, setTiers] = useState({ broad: true, mid: true, niche: true });

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const generate = async () => {
    if (!theme.trim()) {
      toast.error('Add a theme or topic first');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('caption-suggest', {
        body: {
          theme,
          imageUrl,
          dayLabel: 'Standalone',
          postType,
          existingCaption,
        },
      });
      if (invokeErr) throw new Error(invokeErr.message);
      if (!data || data.error) throw new Error(data?.error || 'No result returned');
      setResult(data as AIResult);
    } catch (e: any) {
      setError(e?.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedTags = result
    ? [
        ...(tiers.broad ? result.hashtags.broad : []),
        ...(tiers.mid ? result.hashtags.mid : []),
        ...(tiers.niche ? result.hashtags.niche : []),
      ]
    : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
      {/* Input panel */}
      <div className="bg-white border border-slate-200 p-5 space-y-4 h-fit">
        <div>
          <h3 className="font-serif text-base text-[#1A3B5C] mb-1">Input</h3>
          <p className="font-sans text-xs text-slate-500">
            Fill in the theme and (optional) image URL. The AI writes 3 caption variants and 30 ranked hashtags.
          </p>
        </div>

        <div>
          <label className="block font-sans text-[10px] tracking-wider uppercase text-slate-500 mb-1">
            Theme / Topic *
          </label>
          <input
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="e.g. Morning coffee on the Amalfi coast"
            className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
          />
        </div>

        <div>
          <label className="block font-sans text-[10px] tracking-wider uppercase text-slate-500 mb-1">
            Image URL (optional)
          </label>
          <input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-xs focus:border-[#C9A961] outline-none"
          />
          {imageUrl && (
            <div className="mt-2 border border-slate-200 bg-slate-50 aspect-video overflow-hidden flex items-center justify-center">
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
          {!imageUrl && (
            <div className="mt-2 border border-dashed border-slate-200 bg-slate-50 aspect-video flex items-center justify-center text-slate-300">
              <ImageIcon className="w-6 h-6" />
            </div>
          )}
        </div>

        <div>
          <label className="block font-sans text-[10px] tracking-wider uppercase text-slate-500 mb-1">
            Post Type
          </label>
          <select
            value={postType}
            onChange={(e) => setPostType(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
          >
            {POST_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-sans text-[10px] tracking-wider uppercase text-slate-500 mb-1">
            Existing draft (optional)
          </label>
          <textarea
            value={existingCaption}
            onChange={(e) => setExistingCaption(e.target.value)}
            rows={3}
            placeholder="Paste any rough draft to give the AI a starting point..."
            className="w-full bg-slate-50 border border-slate-200 px-3 py-2 font-sans text-sm focus:border-[#C9A961] outline-none"
          />
        </div>

        <button
          onClick={generate}
          disabled={loading || !theme.trim()}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#C9A961] hover:to-[#8B6F47] disabled:opacity-40 transition-all"
        >
          {loading ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" /> Generate Captions
            </>
          )}
        </button>
      </div>

      {/* Results panel */}
      <div className="bg-white border border-slate-200 p-5 min-h-[400px]">
        {!result && !loading && !error && (
          <div className="h-full flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center mb-4">
              <Sparkles className="w-5 h-5 text-[#C9A961]" />
            </div>
            <h3 className="font-serif text-lg text-[#1A3B5C] mb-1">Ready when you are</h3>
            <p className="font-sans text-xs text-slate-500 max-w-sm">
              Fill in a theme and click Generate. You'll get three caption styles and a 30-tag hashtag set.
            </p>
          </div>
        )}

        {loading && (
          <div className="py-20 text-center">
            <div className="inline-flex items-center gap-3 font-sans text-sm text-slate-500">
              <Sparkles className="w-4 h-4 text-[#C9A961] animate-pulse" />
              Reading inputs and writing variants...
            </div>
            <p className="font-sans text-xs text-slate-400 mt-2">Usually 6–10 seconds.</p>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 font-sans text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-1">Generation failed</div>
              <div className="text-xs">{error}</div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-8">
            <section>
              <h3 className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#1A3B5C] font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" /> Caption Variants
              </h3>
              <div className="space-y-3">
                {result.captions.map((v, idx) => {
                  const key = `cap-${idx}`;
                  return (
                    <div
                      key={key}
                      className={`bg-white border border-slate-200 border-l-4 ${
                        styleAccent[v.style] || 'border-l-slate-400'
                      } p-4`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B6F47] font-semibold">
                          {v.style}
                        </span>
                        <button
                          onClick={() => handleCopy(v.caption, key)}
                          className="px-3 py-1.5 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors flex items-center gap-1"
                        >
                          {copied === key ? (
                            <>
                              <Check className="w-3 h-3" /> Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" /> Copy
                            </>
                          )}
                        </button>
                      </div>
                      <p className="font-sans text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {v.caption}
                      </p>
                      <div className="font-sans text-[10px] text-slate-400 mt-2">
                        {v.caption.length} chars · {v.caption.split(/\s+/).filter(Boolean).length} words
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <h3 className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#1A3B5C] font-semibold mb-3 flex items-center gap-2">
                <Hash className="w-3.5 h-3.5 text-[#C9A961]" /> Hashtag Set · 30 ranked tags
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {(['broad', 'mid', 'niche'] as const).map((tier) => {
                  const accents: Record<string, string> = {
                    broad: 'bg-sky-50 border-sky-200 text-sky-700',
                    mid: 'bg-amber-50 border-amber-200 text-amber-700',
                    niche: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                  };
                  return (
                    <div
                      key={tier}
                      className={`border ${
                        tiers[tier] ? 'border-[#1A3B5C] shadow-sm' : 'border-slate-200 opacity-60'
                      } bg-white p-3 transition-all`}
                    >
                      <button
                        onClick={() => setTiers((s) => ({ ...s, [tier]: !s[tier] }))}
                        className="w-full flex items-start justify-between mb-2 text-left"
                      >
                        <div className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase font-semibold border ${accents[tier]}`}>
                          {tier}
                        </div>
                        <div
                          className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${
                            tiers[tier] ? 'bg-[#1A3B5C] border-[#1A3B5C]' : 'bg-white border-slate-300'
                          }`}
                        >
                          {tiers[tier] && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                      <div className="flex flex-wrap gap-1">
                        {result.hashtags[tier].map((t, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-slate-50 border border-slate-200 font-sans text-[10px] text-slate-600"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="font-sans text-[10px] text-slate-400 mt-2">
                        {result.hashtags[tier].length} tags
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F5F1E8] border border-[#C9A961]/30">
                <div className="min-w-0 flex-1 mr-3">
                  <div className="font-sans text-[10px] tracking-wider uppercase text-[#8B6F47]">
                    {selectedTags.length} of 30 tags selected
                  </div>
                  <div className="font-sans text-xs text-slate-600 mt-1 truncate">
                    {selectedTags.slice(0, 8).join(' ')}
                    {selectedTags.length > 8 ? ' ...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => handleCopy(selectedTags.join(' '), 'tags')}
                  disabled={selectedTags.length === 0}
                  className="px-4 py-2 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors disabled:opacity-40 flex items-center gap-1 flex-shrink-0"
                >
                  {copied === 'tags' ? (
                    <>
                      <Check className="w-3 h-3" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" /> Copy Tags
                    </>
                  )}
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptionTool;
